import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChildren,
} from '@angular/core';
import { Router } from '@angular/router';
import { BreadcrumbService } from '../../../../core/services/breadcrumb/breadcrumb.service';
import { DynamicDialogRef } from '../../../../core/services/dialog';
import { RoleService } from '../../../../core/services/role/role.service';
import { RtoAccessService } from '../../../../core/services/rto/rto-access.service';
import { Breadcrumb } from '../../../models/breadcrumb.model';
import { TranslatePipe } from '../../../pipes/translate/translate.pipe';
import { Card } from '../../cards/card/card';
import { IcHomeComponent } from '../../Icons/ic-home';

/**
 * Horizontal breadcrumb trail shown while Space is held.
 * Opened by the global Space shortcut in `ShortcutService`.
 * Releasing Space opens the highlighted crumb.
 */
@Component({
  selector: 'app-breadcrumb-nav-dialog',
  standalone: true,
  imports: [Card, TranslatePipe, IcHomeComponent],
  templateUrl: './breadcrumb-nav-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class BreadcrumbNavDialogComponent implements OnInit, OnDestroy {
  /** Dialog ref used to dismiss this modal. */
  private readonly dialogRef = inject(DynamicDialogRef);
  /** Current trail from the header breadcrumb source. */
  private readonly breadcrumbService = inject(BreadcrumbService);
  /** Navigates to a selected crumb. */
  private readonly router = inject(Router);
  /** Role checks for RTO routes, matching the header breadcrumb click. */
  private readonly role = inject(RoleService);
  /** Opens the existing RTO access notice when a crumb is blocked. */
  private readonly rtoAccess = inject(RtoAccessService);

  /** Breadcrumb items shown in the modal, same signal as the header. */
  readonly items = this.breadcrumbService.breadcrumbItems;
  /** Home crumb rendered as the home icon. */
  readonly rootItem = computed(() => this.items()[0] ?? null);
  /** Crumbs after home, shown left to right with separators. */
  readonly trailItems = computed(() => this.items().slice(1));
  /** Index of the crumb highlighted for arrow navigation. */
  readonly activeIndex = signal(0);
  /** Crumb buttons, in trail order, for keyboard focus. */
  private readonly crumbButtons = viewChildren<ElementRef<HTMLButtonElement>>('crumb');
  /** Removes the arrow-key listener when the dialog closes. */
  private readonly onArrowKeydown = (event: KeyboardEvent): void => {
    this.handleArrowKeydown(event);
  };
  /** Opens the highlighted crumb when Space is released. */
  private readonly onSpaceKeyup = (event: KeyboardEvent): void => {
    this.confirmActiveCrumb(event);
  };

  constructor() {
    afterNextRender(() => {
      this.focusActiveCrumb();
    });
  }

  /**
   * Highlights the current page crumb and listens for Left, Right, and Space release.
   */
  ngOnInit(): void {
    const last = this.items().length - 1;
    this.activeIndex.set(last >= 0 ? last : 0);
    document.addEventListener('keydown', this.onArrowKeydown, {
      capture: true,
      passive: false,
    });
    document.addEventListener('keyup', this.onSpaceKeyup, {
      capture: true,
      passive: false,
    });
  }

  /**
   * Stops arrow and Space handling when the dialog closes.
   */
  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.onArrowKeydown, true);
    document.removeEventListener('keyup', this.onSpaceKeyup, true);
  }

  /**
   * Closes the breadcrumb modal.
   */
  close(): void {
    this.dialogRef.close();
  }

  /**
   * Moves the highlight one crumb left or right, or opens the highlighted crumb.
   * @param event - Key pressed while the trail is open
   */
  handleArrowKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'Enter') {
      return;
    }
    const target = event.target;
    if (
      target instanceof HTMLElement &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable)
    ) {
      return;
    }
    const count = this.items().length;
    if (count === 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (event.key === 'Enter') {
      const item = this.items()[this.activeIndex()];
      if (item) {
        this.openItem(item);
      }
      return;
    }
    const delta = event.key === 'ArrowRight' ? 1 : -1;
    const next = Math.min(count - 1, Math.max(0, this.activeIndex() + delta));
    this.activeIndex.set(next);
    this.focusActiveCrumb();
  }

  /**
   * Opens the highlighted crumb when Space is released, then closes the trail.
   * A release on the page already open only closes the trail.
   * @param event - Key released while the trail is open
   */
  confirmActiveCrumb(event: KeyboardEvent): void {
    if (
      event.code !== 'Space' ||
      event.shiftKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey
    ) {
      return;
    }
    const target = event.target;
    if (
      target instanceof HTMLElement &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable)
    ) {
      return;
    }
    event.preventDefault();
    const item = this.items()[this.activeIndex()];
    if (item?.route) {
      this.openItem(item);
      return;
    }
    this.close();
  }

  /**
   * Whether a crumb route is the page already open.
   * Query strings are ignored so a list with filters is still the same path.
   * @param route - Absolute route stored on the breadcrumb
   * @returns True when the address matches
   */
  private isCurrentRoute(route: string): boolean {
    return this.pathOf(this.router.url) === this.pathOf(route);
  }

  /**
   * Path portion of a URL, without query, hash, or a trailing slash.
   * @param url - Router URL or breadcrumb route
   * @returns Comparable path
   */
  private pathOf(url: string): string {
    const path = url.split(/[?#]/)[0];
    if (path.length > 1 && path.endsWith('/')) {
      return path.slice(0, -1);
    }
    return path || '/';
  }

  /**
   * Focuses the highlighted crumb without scrolling the page.
   */
  private focusActiveCrumb(): void {
    const button = this.crumbButtons()[this.activeIndex()]?.nativeElement;
    button?.focus({ preventScroll: true });
  }

  /**
   * Navigates to a crumb that has a route, then closes the modal.
   * A crumb for the page already open only closes the modal.
   * @param item - Selected breadcrumb entry
   */
  openItem(item: Breadcrumb): void {
    const route = item.route;
    if (!route) {
      return;
    }
    if (this.isCurrentRoute(route)) {
      this.close();
      return;
    }
    if (this.isRestrictedRtoRoute(route)) {
      this.rtoAccess.openRtoNotConfiguredModal();
      return;
    }
    void this.router.navigateByUrl(route);
    this.dialogRef.close();
  }

  /**
   * Whether an RTO route should be blocked for the current user.
   * Mirrors the header breadcrumb click check.
   * @param route - Absolute route stored on the breadcrumb
   * @returns True when navigation must stay on the RTO access notice
   */
  private isRestrictedRtoRoute(route: string): boolean {
    return (
      !route.startsWith('/dashboard') &&
      (route.startsWith('/rto') || route.includes('rto')) &&
      !this.role.isRtoEnabledForCurrentUser()
    );
  }
}
